import React, { useEffect, useRef } from 'react';
import ensureRecaptchaLoaded from 'components/common/recaptchaLoader.js';

// Inline reCAPTCHA (v2 checkbox) shown IN PLACE of the dieline (or in the download form)
// when the server challenges a level-2 suspected bot with 403 { recaptcha_required,
// recaptcha_site_key }. Rendered in normal page flow -- deliberately NOT a modal -- so
// Google's own image-challenge overlay floats above the page and stays clickable, instead
// of being covered by a competing full-screen layer. On success the parent removes this
// component and re-issues the original request with the token in g-recaptcha-response
// (see DrawingSvg / DownloadConfirmation).

// Reads a user-facing string from the global i18n-js `t` (window.t), falling back to
// English if `t` is absent (e.g. before the runtime loads) or the key is missing.
const label = (key, fallback) => {
  if (typeof t === 'function') {
    const value = t(key);
    if (value && value.indexOf('missing') === -1) { return value; }
  }
  return fallback;
};

const BotChallenge = ({ siteKey, onVerified, onError }) => {
  const widgetRef = useRef(null);
  // reCAPTCHA can only render into an element once; guard against a second render if the
  // effect re-runs (e.g. React StrictMode double-invoke).
  const renderedRef = useRef(false);

  useEffect(() => {
    if (renderedRef.current || !siteKey || !widgetRef.current) { return; }
    renderedRef.current = true;

    ensureRecaptchaLoaded()
      .then((grecaptcha) => {
        grecaptcha.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (token) => { onVerified(token); },
          'error-callback': () => { if (onError) { onError(new Error('recaptcha_error')); } }
        });
      })
      .catch((error) => { if (onError) { onError(error); } });
  }, [siteKey]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#222' }}>
        { label('bot_challenge.heading', 'Please verify you are human') }
      </h3>
      <p style={{ margin: '0 0 18px', fontSize: '14px', color: '#666', lineHeight: 1.45, maxWidth: '360px' }}>
        { label('bot_challenge.description', 'To continue, confirm you are not a robot.') }
      </p>
      <div ref={widgetRef}></div>
    </div>
  );
};

export default BotChallenge;
